import { isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getModelValue, getTrait } from '@aytm/app/stats/lib/stat-page'
import { calcLabelClickTarget, isRType, isTraitType } from '@aytm/app/stats/page/components/lib/utils'
import { hasBase } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/chartModes'
import { calcDisplayRange } from '@aytm/app/stats/page/question-views/oe/lib/display-range'
import {
  calcChartParamsSection,
  calcDefaultChartMode,
  calcSortParamsSection,
  calcViewModeSection,
  calcZoomSection,
  isViewModeSectionStored,
  resolveChartMode,
  resolveSortOrder,
  resolveViewModeParams
} from '@aytm/app/stats/page/question-views/oe/lib/report-params'
import { resolveViewMode, withDefaultConfig } from '@aytm/app/stats/page/question-views/oe/lib/viewModes'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { decodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

// The mode the chart settles on: what it was asked for, else what the question or the report says,
// collapsed to percents for a question with a base. `forParam` is the chart-mode the question opens
// on rather than the one on screen, and that one has no pie to slice when it counts respondents.
const resolveDefaultChartMode = (chartMode, chart, forParam = false) =>
  calcDefaultChartMode({
    chartMode,
    questionChartMode: chart.questionChartMode,
    globalChartMode: getModelValue('chartMode'),
    hasBase: hasBase(chart.answer),
    percentsOnly: true === chart.format?.percentsOnly,
    isRType: forParam && chart.isRType
  })

// The self-heal, resolved against what the report can actually answer: the family's lib says which
// sentiments, codebooks and tags this question still carries, and a mode they no longer support
// falls back to the one it defaults to. `resetAnswer` says the payload the block is holding is not
// the one the resolved mode reads.
const healState = ({ viewMode, config }, { key, type, format, lib }) => {
  const questionKey = lib.getKey(key, config || lib.defaultConfig[viewMode] || {})
  // answering costs a walk of the tag stats, and only the two fallback branches ask
  const fallsBack = 'sentiments' === viewMode || 'tags' === viewMode

  return resolveViewMode({
    viewMode,
    config,
    defaultConfig: lib.defaultConfig,
    presetOptions: lib.presetOptions,
    sentiments: ('sentiments' === viewMode && lib.getQuestionSentiments(questionKey, type)) || [],
    codeBooks: ('tags' === viewMode && lib.getQuestionCodeBooks(questionKey, type)) || [],
    tags:
      ('tags' === viewMode && null != config?.codebook && lib.getQuestionTags(questionKey, type, config.codebook)) ||
      [],
    defaultViewMode: fallsBack ? lib.getDefaultViewMode(questionKey, type, format || {}) : viewMode
  })
}

// Owns the open-ended block state: how the answers are being looked at (the view mode and that
// mode's config), the chart's mode and zoom, and the sort order the shared s<key> section carries.
// Seeded from the question's params and re-seeded whenever any of them changes outside the block.
//
// Which view modes exist, and what this question defaults to, come from the family's own lib - so
// the combo, conversation and menu-test blocks drive this with theirs. A family whose modes are
// always available declines the heal outright.
//
// A correction is announced as a change, so it is written back and the payload it invalidates is
// fetched again.
export default function useOeState({
  params,
  questionKey,
  type,
  variant,
  format,
  lib,
  chartViewModes = [],
  chartModeViewModes,
  questionChartMode,
  defaultSortOrder,
  healViewMode = true,
  answer,
  answerVariants,
  rType,
  isTraitType: isTraitTypeProp,
  labelTarget,
  chartApiRef,
  getWcConfig,
  getWcPreset,
  importWcPreset,
  applyWcPreset,
  getWcParamsSection,
  getExportExtra,
  onReady,
  onStateChange
}) {
  const context = { key: questionKey, type, format, lib, chartViewModes, questionChartMode }

  const contextRef = useRef()
  contextRef.current = { ...context, healViewMode, defaultSortOrder }

  // what the pptx / summary bridges resolve a chart mode and an axis against
  const chartRef = useRef()
  chartRef.current = {
    key: questionKey,
    format,
    answer,
    answerVariants,
    questionChartMode,
    rType: rType || 'cases',
    isRType: isRType({ type, variant }),
    isTraitType: isTraitTypeProp ?? isTraitType({ format, type, variant }),
    chartModeViewModes: chartModeViewModes || chartViewModes
  }

  const needsParamsUpdate = useRef(false)
  const pendingResetAnswer = useRef(false)
  const pendingReason = useRef('seed')

  // a correction landing on top of an announcement not yet made must not soften it: the block
  // reloads for a mode switch, and a seed already carries its own write-back
  const raiseReason = reason => {
    if ('seed' === pendingReason.current || 'mode' === pendingReason.current) return

    pendingReason.current = reason
  }

  // The heal can correct what the params asked for, so the seed is what it settles on.
  const seed = useCallback(source => {
    const { key, type, format, lib, chartViewModes, healViewMode, defaultSortOrder } = contextRef.current

    const stored = resolveViewModeParams({ params: source, key, type, format, lib })
    const { viewMode, config, resetAnswer } = healViewMode
      ? healState(stored, contextRef.current)
      : { ...stored, resetAnswer: false }

    // a corrected mode is not what the params hold, so it has to be written back
    needsParamsUpdate.current =
      needsParamsUpdate.current ||
      !isViewModeSectionStored(
        source,
        calcViewModeSection({ viewMode, config, key, type, format, lib, chartViewModes })
      )

    pendingResetAnswer.current = pendingResetAnswer.current || resetAnswer

    return {
      viewMode,
      config,
      chartMode: resolveChartMode(source),
      zoomLevel: decodeZoomParam(source.z),
      sortOrder: resolveSortOrder(source.s, defaultSortOrder)
    }
    // everything it reads comes off the ref, so it never re-binds
  }, [])

  const [state, setState] = useState(() => seed(params || {}))

  // The tag universe the heal resolves against is the page's, not a prop, so it is re-checked on
  // every pass: a codebook that disappeared under the block corrects the mode on screen the same
  // way a stored param that could not be answered does.
  if (healViewMode) {
    const healed = healState(state, contextRef.current)

    if (healed.viewMode !== state.viewMode || !isEqual(healed.config, state.config)) {
      raiseReason(healed.viewMode === state.viewMode ? 'state' : 'mode')
      pendingResetAnswer.current = pendingResetAnswer.current || healed.resetAnswer

      setState(current => ({ ...current, viewMode: healed.viewMode, config: healed.config }))
    }
  }

  const snapshot = useRef()
  snapshot.current = { state, context, labelTarget, getExportExtra }

  const onModelChange = useCallback(
    (change = {}) =>
      setState(current => {
        if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

        const switched = undefined !== change.viewMode && change.viewMode !== current.viewMode

        // the block rebuilds for a view-mode switch, so it is announced as its own reason
        pendingReason.current = switched ? 'mode' : 'state'

        // a mode carries its own config, so switching to one drops the config of the last -- all but the pinned answer
        if (switched && undefined === change.config) {
          const { defaultConfig } = contextRef.current.lib
          return {
            ...current,
            ...change,
            config: withDefaultConfig(current.config || {}, change.viewMode, defaultConfig)
          }
        }

        return { ...current, ...change }
      }),
    []
  )

  const api = useMemo(() => {
    // the mode the chart opens on once the question and global defaults are applied -- what the
    // exporters draw by. A view with no chart behind it has no mode to name.
    const resolveExportChartMode = () => {
      const chart = chartRef.current
      if (!chart.chartModeViewModes.includes(snapshot.current.state.viewMode)) return null

      return resolveDefaultChartMode(chart.questionChartMode, chart, true)
    }

    // the axis the chart is showing. The rendered chart knows it, zoom included; the maths is the
    // fallback for a chart that has not drawn yet.
    const resolveDisplayRange = (chartMode, zoomLevel) => {
      const chart = chartRef.current
      if (!chart.chartModeViewModes.includes(snapshot.current.state.viewMode)) return null
      if (getTrait() && chart.isTraitType) return null

      const extremes = chartApiRef?.current?.getAxisExtremes?.()
      if (null != extremes) return extremes

      return calcDisplayRange({
        chartMode,
        zoomLevel: zoomLevel || snapshot.current.state.zoomLevel || getModelValue('zoomLevel'),
        globalChartMode: getModelValue('chartMode'),
        format: chart.format,
        answer: chart.answer,
        answerVariants: chart.answerVariants,
        rType: chart.rType,
        confidenceLevels: PageGlobals.CONFIDENCE_LEVELS
      })
    }

    return {
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => {
        const { state, context } = snapshot.current

        return {
          ...calcZoomSection(state.zoomLevel, getModelValue('zoomLevel')),
          // a view or a mode with no axis drops the zoom with it, so both are applied over it
          ...calcViewModeSection({ viewMode: state.viewMode, config: state.config, ...context }),
          ...calcChartParamsSection({
            chartMode: state.chartMode,
            questionChartMode: context.questionChartMode,
            format: context.format,
            defaultChartMode: resolveDefaultChartMode(state.chartMode, chartRef.current)
          }),
          ...calcSortParamsSection(state.sortOrder, defaultSortOrder)
        }
      },
      getExportData: () => {
        const { viewMode, config, sortOrder } = snapshot.current.state
        const chartModeResolved = resolveExportChartMode()

        // a family carrying state of its own spells it out here too - combo's drill-down level
        return {
          viewMode,
          config,
          sortOrder,
          chartModeResolved,
          displayRange: resolveDisplayRange(chartModeResolved, null),
          ...(snapshot.current.getExportExtra?.() || {})
        }
      },
      // pptx / summary bridge: the mode the chart settles on
      getDefaultChartMode: chartMode => resolveDefaultChartMode(chartMode, chartRef.current),
      getDisplayRange: resolveDisplayRange,
      // the tag rows a surface clicks on are resolved by that surface, so they arrive per call
      getLabelClickTarget: (position, answerVariants = []) =>
        calcLabelClickTarget({ ...snapshot.current.labelTarget, answerVariants, position }),
      // the cloud the block is holding: what it is, the bag it hands another one, the bag it
      // takes back, and the section either of them writes down
      getWcConfig,
      getWcPreset,
      importWcPreset,
      applyWcPreset,
      getWcParamsSection,
      update: onModelChange
    }
  }, [
    defaultSortOrder,
    chartApiRef,
    getWcConfig,
    getWcPreset,
    importWcPreset,
    applyWcPreset,
    getWcParamsSection,
    onModelChange
  ])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // A param moved outside the block (another view of the question, the URL, an undo): re-seed.
  const paramsKey = JSON.stringify(params ?? {})
  const lastParamsKey = useRef(paramsKey)
  useEffect(() => {
    if (lastParamsKey.current === paramsKey) return
    lastParamsKey.current = paramsKey

    pendingReason.current = 'seed'
    setState(seed(JSON.parse(paramsKey)))
  }, [paramsKey, seed])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  const announced = useRef(state)

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    const needsUpdate = needsParamsUpdate.current
    needsParamsUpdate.current = false

    const previous = announced.current
    announced.current = state

    // every view reads the payload of the picked turn, so a new turn leaves none of it usable
    const resetAnswer = pendingResetAnswer.current || !isEqual(previous?.config?.ids, state.config?.ids)
    pendingResetAnswer.current = false

    // the payload is fetched per view mode, so the shell reloads when the config naming it moved
    if (reason) {
      onStateChangeRef.current?.(reason, {
        needsUpdate,
        resetAnswer,
        configChanged: !isEqual(previous.config, state.config)
      })
    }
  }, [state])

  return { ...state, onModelChange }
}
